import React, { useState } from 'react'
import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import Tooltip from '@mui/material/Tooltip'
import { ReactComponent as SettingsIcon } from '../../../../assets/images/settings-gear.svg'
import { useIsAdmin } from '../../../../shared/hooks/useIsAdmin'
import { TppSection, TppRow } from '../../types'
import { useTppMutations } from '../../hooks/useTppMutations'
import TppAdminSettingsModal from './TppAdminSettingsModal'

interface TppAdminSettingsButtonProps {
    tppId: string
    sections: TppSection[]
    rows: TppRow[]
    canEdit?: boolean
}

const TppAdminSettingsButton = ({ tppId, sections, rows, canEdit = true }: TppAdminSettingsButtonProps) => {
    const isAdmin = useIsAdmin()
    const [isModalOpen, setIsModalOpen] = useState(false)

    const { createSection, updateSectionTitle, deleteSection, createRow, updateRowTitle, deleteRow } =
        useTppMutations(tppId)

    if (!isAdmin || !canEdit) {
        return null
    }

    const handleCreateSection = async (title: string) => {
        await createSection.mutateAsync({ title })
    }

    const handleUpdateSectionTitle = async (sectionId: string, title: string) => {
        await updateSectionTitle.mutateAsync({ sectionId, title })
    }

    const handleDeleteSection = async (sectionId: string) => {
        await deleteSection.mutateAsync(sectionId)
    }

    const handleCreateRow = async (sectionId: string, title: string) => {
        await createRow.mutateAsync({ sectionId, title })
    }

    const handleUpdateRowTitle = async (rowId: string, title: string) => {
        await updateRowTitle.mutateAsync({ rowId, title })
    }

    const handleDeleteRow = async (rowId: string) => {
        await deleteRow.mutateAsync(rowId)
    }

    return (
        <>
            <Tooltip title="Board Settings (Admin)">
                <IconButton
                    onClick={() => setIsModalOpen(true)}
                    sx={{
                        color: 'figma.grayscale.tier2',
                        '&:hover': {
                            color: 'figma.primary',
                        },
                    }}
                >
                    <SvgIcon component={SettingsIcon} inheritViewBox sx={{ fontSize: '24px' }} />
                </IconButton>
            </Tooltip>

            <TppAdminSettingsModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                sections={sections}
                rows={rows}
                onCreateSection={handleCreateSection}
                onUpdateSectionTitle={handleUpdateSectionTitle}
                onDeleteSection={handleDeleteSection}
                onCreateRow={handleCreateRow}
                onUpdateRowTitle={handleUpdateRowTitle}
                onDeleteRow={handleDeleteRow}
                isCreatingSection={createSection.isLoading}
                isUpdatingSection={updateSectionTitle.isLoading}
                isDeletingSection={deleteSection.isLoading}
                isCreatingRow={createRow.isLoading}
                isUpdatingRow={updateRowTitle.isLoading}
                isDeletingRow={deleteRow.isLoading}
            />
        </>
    )
}

export default TppAdminSettingsButton
